import { usePlatform } from '@/hooks/usePlatform'
import type { Platform } from '@/contexts/ProtocolContext/ProtocolContext'

/** High-level protocol family, independent of the specific platform/service. */
export type ProtocolFamily = 'atproto' | 'activitypub' | 'nostr'

/** Map a specific platform to its protocol family. */
export function getPlatformProtocol(platform: Platform): ProtocolFamily {
    switch (platform) {
        case 'bluesky':
            return 'atproto'
        case 'mastodon':
        case 'pixelfed':
            return 'activitypub'
        case 'nostr':
            return 'nostr'
    }
}

/**
 * Returns `true` when the currently selected platform belongs to one of the
 * allowed protocol families.
 *
 * Use this to guard hooks that are only relevant to a subset of protocols:
 *
 * ```ts
 * // atproto-only hook
 * const isCompatible = usePlatformGuard('atproto')
 * const enabled = !!session && isCompatible
 *
 * // atproto or ActivityPub hook
 * const isCompatible = usePlatformGuard('atproto', 'activitypub')
 * ```
 */
export function usePlatformGuard(...allowedProtocols: ProtocolFamily[]): boolean {
    const { selectedProtocol } = usePlatform()
    return allowedProtocols.includes(getPlatformProtocol(selectedProtocol))
}
